import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Stack, Card, CardContent, Typography, TextField, Button, IconButton, Divider,
  MenuItem, Select, FormControlLabel, Switch, Alert, Snackbar, CircularProgress, Tooltip,
  Accordion, AccordionSummary, AccordionDetails, Chip,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import type {
  Api, TenantView, SiteChrome, ChromeNavItem, ChromeNavChild, ChromeNavColumn,
  ChromeCta, ChromeFooterColumn, ChromeSocial,
} from './api';
import { useImagePicker } from './MediaPicker';

// Seed used ONLY when the site has no saved chrome yet, so the first save
// persists the current live look instead of wiping the header/footer. Kept in
// sync with apps/web/src/chrome.ts (defaultChrome).
const STARTER: SiteChrome = {
  logoText: 'Cymmetri',
  nav: [
    { label: 'Products', children: [
      { label: 'Identity Control Plane', href: '/identity-control-plane', description: 'Unify every identity' },
      { label: 'Workforce Identity', href: '/workforce-identity' },
      { label: 'Customer Identity', href: '/customer-identity' },
    ] },
    { label: 'Pricing', href: '/pricing' },
    { label: 'Resources', children: [
      { label: 'Docs', href: '/docs' },
      { label: 'Case Studies', href: '/case-studies' },
      { label: 'Insights', href: '/insights' },
    ] },
    { label: 'Company', href: '/about-us' },
  ],
  headerCtas: [
    { label: 'Log in', href: 'https://login.cymmetri.io', variant: 'ghost' },
    { label: 'Get a Demo', href: '#contact', variant: 'primary' },
  ],
  footerColumns: [
    { title: 'Product', links: [
      { label: 'Identity Control Plane', href: '/identity-control-plane' },
      { label: 'Pricing', href: '/pricing' },
      { label: 'Integrations', href: '/key-integrations' },
    ] },
    { title: 'Company', links: [
      { label: 'About', href: '/about-us' },
      { label: 'Careers', href: '/careers' },
      { label: 'Contact', href: '/contact-us' },
    ] },
    { title: 'Resources', links: [
      { label: 'Docs', href: '/docs' },
      { label: 'Case Studies', href: '/case-studies' },
      { label: 'White Papers', href: '/white-papers' },
    ] },
  ],
  socials: [
    { label: 'LinkedIn', href: 'https://www.linkedin.com/company/cymmetri/' },
    { label: 'Twitter', href: 'https://twitter.com/Cymmetri' },
    { label: 'Instagram', href: 'https://instagram.com/cymmetri.social' },
  ],
  legalLinks: [
    { label: 'Privacy Policy', href: '/privacy-policy' },
    { label: 'Terms & Conditions', href: '/terms' },
  ],
  copyright: '© 2026 Cymmetri. All rights reserved.',
};

// --- immutable list helpers ---
function move<T>(arr: T[], i: number, dir: -1 | 1): T[] {
  const j = i + dir;
  if (j < 0 || j >= arr.length) return arr;
  const next = arr.slice();
  const a = next[i] as T;
  next[i] = next[j] as T;
  next[j] = a;
  return next;
}
function setAt<T>(arr: T[], i: number, v: T): T[] {
  const next = arr.slice();
  next[i] = v;
  return next;
}
function removeAt<T>(arr: T[], i: number): T[] {
  const next = arr.slice();
  next.splice(i, 1);
  return next;
}

// Compact toolbar for a list row: move up/down + delete.
function RowTools({ i, len, onMove, onDelete }: { i: number; len: number; onMove: (d: -1 | 1) => void; onDelete: () => void }) {
  return (
    <Stack direction="row" spacing={0} sx={{ flexShrink: 0 }}>
      <IconButton size="small" disabled={i === 0} onClick={() => onMove(-1)}><ArrowUpwardIcon fontSize="small" /></IconButton>
      <IconButton size="small" disabled={i === len - 1} onClick={() => onMove(1)}><ArrowDownwardIcon fontSize="small" /></IconButton>
      <IconButton size="small" color="error" onClick={onDelete}><DeleteOutlineIcon fontSize="small" /></IconButton>
    </Stack>
  );
}

function navKind(it: ChromeNavItem): 'link' | 'dropdown' | 'megamenu' {
  if (it.columns && it.columns.length) return 'megamenu';
  if (it.children && it.children.length) return 'dropdown';
  return 'link';
}

// Themes whose pages carry their OWN header/footer as page blocks (ia*/k*) and
// render bare — so the shared Shell chrome edited here does NOT apply to them.
// Keep in sync with the bare-render branch in apps/web/src/server.ts.
const SELF_CHROME_THEMES = new Set(['intelliauth', 'kathreftis']);

export function ChromeEditor({ api, tenant }: { api: Api; tenant: TenantView }) {
  const t = tenant.tenantId;
  const picker = useImagePicker();
  const [chrome, setChrome] = useState<SiteChrome | null>(null);
  const [theme, setTheme] = useState<string>('');
  const [usingDefaults, setUsingDefaults] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ msg: string; sev: 'success' | 'error' } | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const res = await api.chrome(t);
        if (!alive) return;
        setTheme(res?.theme ?? '');
        if (res?.chrome) {
          setChrome(res.chrome);
          setUsingDefaults(false);
        } else {
          setChrome(JSON.parse(JSON.stringify(STARTER)));
          setUsingDefaults(true);
        }
      } catch {
        if (alive) setToast({ msg: 'Failed to load header/footer', sev: 'error' });
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [api, t]);

  const patch = useCallback((p: Partial<SiteChrome>) => setChrome((c) => (c ? { ...c, ...p } : c)), []);

  const save = async () => {
    if (!chrome) return;
    if (!chrome.logoText?.trim()) { setToast({ msg: 'Logo text is required', sev: 'error' }); return; }
    setSaving(true);
    try {
      await api.saveChrome(t, chrome);
      setUsingDefaults(false);
      setToast({ msg: 'Header & footer saved — live on the next page load', sev: 'success' });
    } catch (e: any) {
      setToast({ msg: e?.message || 'Save failed', sev: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const pickInto = async (set: (url: string) => void) => {
    if (!picker) return;
    const url = await picker.pick();
    if (url) set(url);
  };

  if (loading || !chrome) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>;
  }

  // Self-chrome tenants (IntelliAuth, Kathrefits) render their pages bare — the
  // header/footer are page BLOCKS, not this shared Shell chrome. Editing here would
  // do nothing, so point the user to the right place instead of the inert form.
  if (SELF_CHROME_THEMES.has(theme)) {
    return (
      <Box sx={{ maxWidth: 720, mx: 'auto', pt: 4 }}>
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography sx={{ fontWeight: 600, mb: 0.5 }}>
            This tenant’s header &amp; footer are page blocks, not site chrome.
          </Typography>
          Its pages ship their own navigation and footer components (e.g.{' '}
          <code>{theme === 'kathreftis' ? 'K Nav / K Footer' : 'IA Nav / IA Footer'}</code>) and render
          without the shared Shell chrome — so this screen does <b>not</b> affect the live site.
          Edit them under <b>Pages</b> → open any page → the <b>Nav</b> and <b>Footer</b> sections.
        </Alert>
      </Box>
    );
  }

  const nav = chrome.nav ?? [];
  const ctas = chrome.headerCtas ?? [];
  const cols = chrome.footerColumns ?? [];
  const socials = chrome.socials ?? [];
  const legal = chrome.legalLinks ?? [];
  const contact = chrome.footerContact ?? {};
  const ann = chrome.announcement;

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', pb: 12 }}>
      {usingDefaults && (
        <Alert severity="info" sx={{ mb: 2 }}>
          This site has no saved header/footer yet — you’re editing the current built-in defaults.
          Saving will make them editable content for good.
        </Alert>
      )}

      {/* Branding */}
      <Card sx={{ mb: 2 }}><CardContent>
        <Typography variant="h6" gutterBottom>Branding</Typography>
        <Stack spacing={2}>
          <TextField label="Logo text" size="small" fullWidth value={chrome.logoText}
            onChange={(e) => patch({ logoText: e.target.value })} />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField label="Header logo URL" size="small" fullWidth value={chrome.logoUrl ?? ''}
              onChange={(e) => patch({ logoUrl: e.target.value || undefined })}
              InputProps={{ endAdornment: (
                <Tooltip title="Pick from media"><IconButton size="small" onClick={() => pickInto((u) => patch({ logoUrl: u }))}><ImageOutlinedIcon fontSize="small" /></IconButton></Tooltip>
              ) }} />
            <TextField label="Footer logo URL (light variant)" size="small" fullWidth value={chrome.footerLogoUrl ?? ''}
              onChange={(e) => patch({ footerLogoUrl: e.target.value || undefined })}
              InputProps={{ endAdornment: (
                <Tooltip title="Pick from media"><IconButton size="small" onClick={() => pickInto((u) => patch({ footerLogoUrl: u }))}><ImageOutlinedIcon fontSize="small" /></IconButton></Tooltip>
              ) }} />
          </Stack>
        </Stack>
      </CardContent></Card>

      {/* Announcement bar */}
      <Card sx={{ mb: 2 }}><CardContent>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">Announcement bar</Typography>
          <FormControlLabel control={<Switch checked={!!ann} onChange={(e) =>
            patch({ announcement: e.target.checked ? (ann ?? { text: '' }) : undefined })} />} label="Show" />
        </Stack>
        {ann && (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Text" size="small" fullWidth value={ann.text}
              onChange={(e) => patch({ announcement: { ...ann, text: e.target.value } })} />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField label="Link text" size="small" fullWidth value={ann.linkText ?? ''}
                onChange={(e) => patch({ announcement: { ...ann, linkText: e.target.value || undefined } })} />
              <TextField label="Link URL" size="small" fullWidth value={ann.linkHref ?? ''}
                onChange={(e) => patch({ announcement: { ...ann, linkHref: e.target.value || undefined } })} />
            </Stack>
            <FormControlLabel control={<Switch checked={!!ann.external} onChange={(e) =>
              patch({ announcement: { ...ann, external: e.target.checked || undefined } })} />} label="Open link in new tab" />
          </Stack>
        )}
      </CardContent></Card>

      {/* Header navigation */}
      <Card sx={{ mb: 2 }}><CardContent>
        <Typography variant="h6" gutterBottom>Header navigation</Typography>
        <Stack spacing={1.5}>
          {nav.map((it, i) => {
            const kind = navKind(it);
            const setItem = (v: ChromeNavItem) => patch({ nav: setAt(nav, i, v) });
            return (
              <Accordion key={i} disableGutters variant="outlined">
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1 }}>
                    <Typography sx={{ fontWeight: 600 }}>{it.label || '(untitled)'}</Typography>
                    <Chip size="small" label={kind} />
                  </Stack>
                  <Box onClick={(e) => e.stopPropagation()}>
                    <RowTools i={i} len={nav.length} onMove={(d) => patch({ nav: move(nav, i, d) })} onDelete={() => patch({ nav: removeAt(nav, i) })} />
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={2}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                      <TextField label="Label" size="small" fullWidth value={it.label}
                        onChange={(e) => setItem({ ...it, label: e.target.value })} />
                      <Select size="small" value={kind} sx={{ minWidth: 160 }} onChange={(e) => {
                        const k = e.target.value as 'link' | 'dropdown' | 'megamenu';
                        if (k === 'link') setItem({ label: it.label, href: it.href ?? '', external: it.external });
                        else if (k === 'dropdown') setItem({ label: it.label, children: it.children ?? [] });
                        else setItem({ label: it.label, columns: it.columns ?? [] });
                      }}>
                        <MenuItem value="link">Simple link</MenuItem>
                        <MenuItem value="dropdown">Dropdown</MenuItem>
                        <MenuItem value="megamenu">Mega-menu</MenuItem>
                      </Select>
                    </Stack>

                    {kind === 'link' && (
                      <Stack direction="row" spacing={2} alignItems="center">
                        <TextField label="URL" size="small" fullWidth value={it.href ?? ''}
                          onChange={(e) => setItem({ ...it, href: e.target.value })} />
                        <FormControlLabel control={<Switch checked={!!it.external} onChange={(e) => setItem({ ...it, external: e.target.checked || undefined })} />} label="New tab" />
                      </Stack>
                    )}

                    {kind === 'dropdown' && (
                      <LinkListEditor
                        title="Dropdown links" withDescription
                        items={it.children ?? []}
                        onChange={(children) => setItem({ ...it, children })}
                      />
                    )}

                    {kind === 'megamenu' && (
                      <ColumnsEditor columns={it.columns ?? []} onChange={(columns) => setItem({ ...it, columns })} />
                    )}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            );
          })}
          <Button startIcon={<AddIcon />} size="small" onClick={() => patch({ nav: [...nav, { label: 'New item', href: '/' }] })}>Add nav item</Button>
        </Stack>
      </CardContent></Card>

      {/* Header CTAs */}
      <Card sx={{ mb: 2 }}><CardContent>
        <Typography variant="h6" gutterBottom>Header buttons</Typography>
        <Stack spacing={1.5}>
          {ctas.map((c, i) => {
            const setC = (v: ChromeCta) => patch({ headerCtas: setAt(ctas, i, v) });
            return (
              <Stack key={i} direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
                <TextField label="Label" size="small" value={c.label} sx={{ flex: 1 }} onChange={(e) => setC({ ...c, label: e.target.value })} />
                <TextField label="URL" size="small" value={c.href} sx={{ flex: 2 }} onChange={(e) => setC({ ...c, href: e.target.value })} />
                <Select size="small" value={c.variant ?? 'primary'} sx={{ minWidth: 120 }} onChange={(e) => setC({ ...c, variant: e.target.value as 'primary' | 'ghost' })}>
                  <MenuItem value="primary">Primary</MenuItem>
                  <MenuItem value="ghost">Ghost</MenuItem>
                </Select>
                <FormControlLabel control={<Switch checked={!!c.external} onChange={(e) => setC({ ...c, external: e.target.checked || undefined })} />} label="New tab" />
                <RowTools i={i} len={ctas.length} onMove={(d) => patch({ headerCtas: move(ctas, i, d) })} onDelete={() => patch({ headerCtas: removeAt(ctas, i) })} />
              </Stack>
            );
          })}
          <Button startIcon={<AddIcon />} size="small" onClick={() => patch({ headerCtas: [...ctas, { label: 'Button', href: '/', variant: 'primary' }] })}>Add button</Button>
        </Stack>
      </CardContent></Card>

      {/* Footer columns */}
      <Card sx={{ mb: 2 }}><CardContent>
        <Typography variant="h6" gutterBottom>Footer columns</Typography>
        <Stack spacing={2}>
          {cols.map((col, i) => {
            const setCol = (v: ChromeFooterColumn) => patch({ footerColumns: setAt(cols, i, v) });
            return (
              <Box key={i} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <TextField label="Column title" size="small" fullWidth value={col.title} onChange={(e) => setCol({ ...col, title: e.target.value })} />
                  <RowTools i={i} len={cols.length} onMove={(d) => patch({ footerColumns: move(cols, i, d) })} onDelete={() => patch({ footerColumns: removeAt(cols, i) })} />
                </Stack>
                <LinkListEditor title="Links" items={col.links} onChange={(links) => setCol({ ...col, links })} />
              </Box>
            );
          })}
          <Button startIcon={<AddIcon />} size="small" onClick={() => patch({ footerColumns: [...cols, { title: 'New column', links: [] }] })}>Add column</Button>
        </Stack>
      </CardContent></Card>

      {/* Footer contact */}
      <Card sx={{ mb: 2 }}><CardContent>
        <Typography variant="h6" gutterBottom>Footer contact</Typography>
        <Stack spacing={2}>
          <TextField label="Heading (e.g. “For sales queries:”)" size="small" fullWidth value={contact.title ?? ''}
            onChange={(e) => patch({ footerContact: { ...contact, title: e.target.value || undefined } })} />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField label="Email label" size="small" fullWidth value={contact.emailText ?? ''} onChange={(e) => patch({ footerContact: { ...contact, emailText: e.target.value || undefined } })} />
            <TextField label="Email" size="small" fullWidth value={contact.email ?? ''} onChange={(e) => patch({ footerContact: { ...contact, email: e.target.value || undefined } })} />
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField label="Phone label" size="small" fullWidth value={contact.phoneText ?? ''} onChange={(e) => patch({ footerContact: { ...contact, phoneText: e.target.value || undefined } })} />
            <TextField label="Phone" size="small" fullWidth value={contact.phone ?? ''} onChange={(e) => patch({ footerContact: { ...contact, phone: e.target.value || undefined } })} />
          </Stack>
        </Stack>
      </CardContent></Card>

      {/* Socials */}
      <Card sx={{ mb: 2 }}><CardContent>
        <Typography variant="h6" gutterBottom>Social links</Typography>
        <Stack spacing={1.5}>
          {socials.map((s, i) => {
            const setS = (v: ChromeSocial) => patch({ socials: setAt(socials, i, v) });
            return (
              <Stack key={i} direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
                <TextField label="Network" size="small" value={s.label} sx={{ flex: 1 }} onChange={(e) => setS({ ...s, label: e.target.value })} />
                <TextField label="URL" size="small" value={s.href} sx={{ flex: 2 }} onChange={(e) => setS({ ...s, href: e.target.value })} />
                <TextField label="Icon URL (optional)" size="small" value={s.iconUrl ?? ''} sx={{ flex: 2 }} onChange={(e) => setS({ ...s, iconUrl: e.target.value || undefined })}
                  InputProps={{ endAdornment: (<IconButton size="small" onClick={() => pickInto((u) => setS({ ...s, iconUrl: u }))}><ImageOutlinedIcon fontSize="small" /></IconButton>) }} />
                <RowTools i={i} len={socials.length} onMove={(d) => patch({ socials: move(socials, i, d) })} onDelete={() => patch({ socials: removeAt(socials, i) })} />
              </Stack>
            );
          })}
          <Button startIcon={<AddIcon />} size="small" onClick={() => patch({ socials: [...socials, { label: 'LinkedIn', href: '' }] })}>Add social</Button>
        </Stack>
      </CardContent></Card>

      {/* Legal + copyright */}
      <Card sx={{ mb: 2 }}><CardContent>
        <Typography variant="h6" gutterBottom>Legal &amp; copyright</Typography>
        <LinkListEditor title="Legal links" items={legal} onChange={(v) => patch({ legalLinks: v as any })} />
        <TextField label="Copyright line" size="small" fullWidth sx={{ mt: 2 }} value={chrome.copyright ?? ''}
          onChange={(e) => patch({ copyright: e.target.value || undefined })} />
      </CardContent></Card>

      {/* Sticky save bar */}
      <Box sx={{ position: 'sticky', bottom: 0, py: 2, bgcolor: 'background.default', borderTop: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <Button variant="contained" onClick={save} disabled={saving} startIcon={saving ? <CircularProgress size={16} /> : undefined}>
          {saving ? 'Saving…' : 'Save header & footer'}
        </Button>
      </Box>

      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        {toast ? <Alert severity={toast.sev} onClose={() => setToast(null)}>{toast.msg}</Alert> : undefined}
      </Snackbar>
    </Box>
  );
}

// A reusable editor for a list of {label, href, description?, external?} links.
function LinkListEditor({ title, items, onChange, withDescription }: {
  title: string;
  items: ChromeNavChild[];
  onChange: (items: ChromeNavChild[]) => void;
  withDescription?: boolean;
}) {
  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>{title}</Typography>
      <Stack spacing={1}>
        {items.map((l, i) => {
          const setL = (v: ChromeNavChild) => onChange(setAt(items, i, v));
          return (
            <Stack key={i} direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }}>
              <TextField label="Label" size="small" value={l.label} sx={{ flex: 1 }} onChange={(e) => setL({ ...l, label: e.target.value })} />
              <TextField label="URL" size="small" value={l.href} sx={{ flex: 1.5 }} onChange={(e) => setL({ ...l, href: e.target.value })} />
              {withDescription && (
                <TextField label="Description" size="small" value={l.description ?? ''} sx={{ flex: 1.5 }} onChange={(e) => setL({ ...l, description: e.target.value || undefined })} />
              )}
              <FormControlLabel control={<Switch size="small" checked={!!l.external} onChange={(e) => setL({ ...l, external: e.target.checked || undefined })} />} label="New tab" />
              <RowTools i={i} len={items.length} onMove={(d) => onChange(move(items, i, d))} onDelete={() => onChange(removeAt(items, i))} />
            </Stack>
          );
        })}
        <Button startIcon={<AddIcon />} size="small" onClick={() => onChange([...items, { label: 'New link', href: '/' }])}>Add link</Button>
      </Stack>
    </Box>
  );
}

// Mega-menu columns: each column has an optional title/href + a list of links.
function ColumnsEditor({ columns, onChange }: { columns: ChromeNavColumn[]; onChange: (c: ChromeNavColumn[]) => void }) {
  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>Mega-menu columns</Typography>
      <Stack spacing={2}>
        {columns.map((col, i) => {
          const setCol = (v: ChromeNavColumn) => onChange(setAt(columns, i, v));
          return (
            <Box key={i} sx={{ p: 1.5, border: '1px dashed', borderColor: 'divider', borderRadius: 1 }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <TextField label="Column heading (optional)" size="small" fullWidth value={col.label ?? ''} onChange={(e) => setCol({ ...col, label: e.target.value || undefined })} />
                <TextField label="Heading link (optional)" size="small" fullWidth value={col.href ?? ''} onChange={(e) => setCol({ ...col, href: e.target.value || undefined })} />
                <RowTools i={i} len={columns.length} onMove={(d) => onChange(move(columns, i, d))} onDelete={() => onChange(removeAt(columns, i))} />
              </Stack>
              <LinkListEditor title="Links" withDescription items={col.links} onChange={(links) => setCol({ ...col, links })} />
            </Box>
          );
        })}
        <Button startIcon={<AddIcon />} size="small" onClick={() => onChange([...columns, { links: [] }])}>Add column</Button>
      </Stack>
    </Box>
  );
}
